// Subscription state derivation.
//
// Pure functions that take raw RC events and Stripe subscription/payment docs
// and return the derived state (will_renew, status, etc.) the UI needs.
// Called from AuthProvider's Firestore listeners on customers/{uid}/subscriptions
// and revenuecat/{uid}/events, which fire on every webhook write — so derivation
// stays current without needing server-side Cloud Function triggers.

export const STATUS = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  BILLING_RETRY: 'billing_retry',
  GRACE: 'grace',
  PAUSED: 'paused',
  EXPIRED: 'expired',
  REFUNDED: 'refunded',
  ONE_TIME: 'one_time',
};

const RC_EVENT_DERIVATION = {
  INITIAL_PURCHASE:    { will_renew: true,  status: STATUS.ACTIVE },
  RENEWAL:             { will_renew: true,  status: STATUS.ACTIVE },
  UNCANCELLATION:      { will_renew: true,  status: STATUS.ACTIVE },
  PRODUCT_CHANGE:      { will_renew: true,  status: STATUS.ACTIVE },
  NON_RENEWING_PURCHASE: { will_renew: false, status: STATUS.ONE_TIME },
  CANCELLATION:        { will_renew: false, status: STATUS.CANCELLED },
  BILLING_ISSUE:       { will_renew: true,  status: STATUS.BILLING_RETRY },
  EXPIRATION:          { will_renew: false, status: STATUS.EXPIRED },
  SUBSCRIPTION_PAUSED: { will_renew: false, status: STATUS.PAUSED },
  REFUND:              { will_renew: false, status: STATUS.REFUNDED },
};

const RC_STORE_TO_PAYMENT_MODE = {
  APP_STORE: 'app_store',
  PLAY_STORE: 'play_store',
  MAC_APP_STORE: 'app_store',
  AMAZON: 'play_store',
  STRIPE: 'stripe',
  PROMOTIONAL: 'app_store',
};

// Canonical `paymentMode` from a RevenueCat `store` value (event webhook or SDK
// EntitlementInfo). Always lowercase ('app_store'/'play_store'/'stripe') so web +
// mobile docs and the UIs that key off paymentMode agree. RevenueCat emits the
// store in mixed casing across surfaces (webhook vs REST vs SDK enum); the map
// covers the known canonical stores and the .toLowerCase() fallback keeps any
// unmapped/lowercase value usable rather than dropping to null.
export function normalizeRcStore(store) {
  return RC_STORE_TO_PAYMENT_MODE[store] || (store || '').toLowerCase() || null;
}

/**
 * Normalize a timestamp of unknown shape to epoch milliseconds.
 * Accepts: null/undefined (→ 0), a number in seconds (< 1e12, scaled to ms) or
 * milliseconds, a Firestore Timestamp ({ toDate() } or { seconds, nanoseconds }),
 * a Date, or a date string. Returns 0 for unparseable input.
 * @param {null|undefined|number|string|Date|{toDate?:Function,seconds?:number,nanoseconds?:number}} value
 * @returns {number} epoch milliseconds
 */
export function toMs(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // A numeric epoch string ("1717200000" / "4102444800000") must be handled as
    // a number — new Date() rejects it as an invalid date. Apply the same
    // seconds→ms scaling as the numeric branch. Non-numeric strings (ISO dates)
    // fall through to Date parsing.
    if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
      const n = Number(trimmed);
      return n < 1e12 ? n * 1000 : n;
    }
    const parsed = new Date(trimmed).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isActiveAt(expirationMs, now = Date.now()) {
  return expirationMs > now;
}

// Statuses that revoke premium even when subscription_expiration is still in the
// future: a refund, or a non-payment terminal state where Stripe's optimistic
// period_end may have outlived the paid-through date.
const PREMIUM_DENY_STATUSES = new Set([STATUS.REFUNDED, STATUS.EXPIRED, STATUS.GRACE]);

// Single source of truth for "does this subscription doc grant premium right now".
// Used by BOTH apps' auth providers so web and mobile gate identically.
//   - billing_retry  → granted: grace period while Stripe is still retrying the
//     card. Access ends when Stripe gives up and moves the sub to canceled/unpaid
//     (→ expired/grace), NOT on a fixed timer.
//   - refunded/expired/grace → denied, regardless of expiration.
//   - everything else (active/cancelled-but-paid/paused/one_time) → gated on
//     subscription_expiration still being in the future.
export function isPremiumSubscription(sub, now = Date.now()) {
  if (!sub) return false;
  if (sub.status === STATUS.BILLING_RETRY) return true;
  if (PREMIUM_DENY_STATUSES.has(sub.status)) return false;
  // toMs (not parseInt) so expirations stored as seconds, a Date, a Firestore
  // Timestamp, or an ISO string normalize correctly — parseInt would misread
  // those and silently deny a paying user. Matches the mobile gate this replaces.
  return isActiveAt(toMs(sub.subscription_expiration), now);
}

// White-label entitlement is per-exam: the record at `userDoc.subscriptions[examKey]`. Normalize
// it to the shape isPremiumSubscription expects — `subscription_expiration` in millis and a
// `status`. Default the status to 'active' but PRESERVE any stored deny status
// (refunded/expired/grace) so revocation-before-expiry stays expressible. Returns null when the
// user has no record for that exam. Shared so web + mobile white-label gate identically.
export function resolveExamSubscription(userDoc, examKey) {
  const sub = examKey && userDoc && userDoc.subscriptions ? userDoc.subscriptions[examKey] : null;
  if (!sub) return null;
  return {
    ...sub,
    status: sub.status ?? STATUS.ACTIVE,
    subscription_expiration: toMs(sub.subscription_expiration),
  };
}

// White-label per-exam entitlement check: does `userDoc` own a currently-premium
// subscription for `examKey`? The single rule both apps' gates (mobile Tabbar/
// NotSubscribed/NewAuthProvider, web Router/NotSubscribed/AuthProvider via
// activeSubscriptionFor) must agree on — keep it here, not inlined per call site, so a
// future change to the gating logic (e.g. a new deny status) lands everywhere at once.
export function isExamEntitled(userDoc, examKey, now = Date.now()) {
  return isPremiumSubscription(resolveExamSubscription(userDoc, examKey), now);
}

export function planFromStripeInterval(interval, intervalCount) {
  const normalized = (interval || '').toLowerCase();
  if (normalized === 'month') {
    if (intervalCount === 1) return 'Monthly';
    if (intervalCount === 3) return 'Quarterly';
    if (intervalCount === 6) return 'Semi-Annual';
  }
  if (normalized === 'year') return 'Annual';
  return `${intervalCount || ''} ${normalized || 'period'}(s)`.trim();
}

// Map a single RevenueCat event type to its {will_renew, status}. Single source
// of truth for the RC event→state mapping, shared so the app SDK paths (which
// already hold the latest event/entitlement) don't re-derive their own
// convention. Unknown types default to active (treat an unrecognized event as a
// live sub rather than wrongly revoking) — same fallback as deriveRevenueCatState.
export function deriveRcEventState(eventType) {
  return RC_EVENT_DERIVATION[eventType] || { will_renew: null, status: STATUS.ACTIVE };
}

// The RevenueCat SDK path (react-native-purchases `EntitlementInfo`) has no event
// TYPE — derive the same {will_renew, status} from the entitlement flags so the
// events path (deriveRcEventState) and the SDK path can't drift. A billing issue
// wins (grace period); otherwise willRenew===false means canceled-but-active.
// Unknown/missing entitlement defaults to active, matching deriveRcEventState.
export function deriveRcEntitlementState(entitlement) {
  if (!entitlement) return { will_renew: null, status: STATUS.ACTIVE };
  if (entitlement.billingIssueDetectedAt) {
    return { will_renew: true, status: STATUS.BILLING_RETRY };
  }
  if (entitlement.willRenew === false) {
    return { will_renew: false, status: STATUS.CANCELLED };
  }
  return { will_renew: entitlement.willRenew, status: STATUS.ACTIVE };
}

// RC: pick the latest event for our allowed product IDs and derive state.
export function deriveRevenueCatState(events, allowedProductIds) {
  if (!Array.isArray(events) || events.length === 0) return null;
  if (!Array.isArray(allowedProductIds) || allowedProductIds.length === 0) return null;
  const ours = events.filter((e) => allowedProductIds.includes(e.product_id));
  if (ours.length === 0) return null;
  const latest = ours
    .slice()
    .sort((a, b) => (b.event_timestamp_ms || 0) - (a.event_timestamp_ms || 0))[0];
  const mapping = RC_EVENT_DERIVATION[latest.type] || { will_renew: null, status: STATUS.ACTIVE };
  return {
    source: 'revenuecat',
    subscription_expiration: toMs(latest.expiration_at_ms),
    paymentMode: normalizeRcStore(latest.store),
    paymentType: mapping.status === STATUS.ONE_TIME ? 'one_time' : 'recurring',
    will_renew: mapping.will_renew,
    status: mapping.status,
    product_id: latest.product_id,
    derived_event_id: latest.id || latest.doc_id || null,
    derived_event_ts: latest.event_timestamp_ms || 0,
  };
}

// Stripe: pick the newest active-or-recent recurring sub for our product and derive state.
const STRIPE_ACTIVE_STATUSES = ['active', 'trialing'];
// Rank for choosing the representative sub when a customer has several. Prefer a
// sub that currently GRANTS access, so a dead/incomplete doc can't shadow a live
// one: active/trialing (paid) > past_due (billing_retry grace) > everything else
// (incomplete / incomplete_expired / canceled / unpaid — none grant premium).
// Without the past_due tier, a fresh `incomplete` retry (optimistic future
// period_end) would out-sort a still-in-grace past_due sub and wrongly deny it.
function stripeSelectionRank(sub) {
  if (STRIPE_ACTIVE_STATUSES.includes(sub.status)) return 2;
  if (sub.status === 'past_due') return 1;
  return 0;
}
export function deriveStripeState(subscriptions) {
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) return null;
  // Prefer a granting sub (active/trialing, then past_due grace) over any stale
  // canceled/expired/incomplete one, THEN break ties by latest period end.
  // Without the status preference, a canceled sub with a later current_period_end
  // (e.g. a canceled annual sitting next to a fresh monthly) — or a dead
  // incomplete retry next to a past_due grace sub — would win and wrongly deny a
  // still-entitled user.
  const sorted = subscriptions.slice().sort((a, b) => {
    const rankDelta = stripeSelectionRank(b) - stripeSelectionRank(a);
    if (rankDelta !== 0) return rankDelta;
    return toMs(b.current_period_end) - toMs(a.current_period_end);
  });
  const sub = sorted[0];
  // Stripe advances current_period_end to the NEXT period the moment it generates
  // the renewal invoice — before that invoice is actually paid. So for any state
  // reached via a failed/unpaid renewal (past_due / unpaid / canceled), period_end
  // points at a period the user never paid for; current_period_start is the
  // last-paid boundary. Crediting period_end there would hand out a free unpaid
  // period (the billing_retry entitlement leak). Only the paid states
  // (active / trialing, including a paid sub set to cancel_at_period_end) may use
  // period_end — that period was paid for and access should run to its end.
  const periodEndMs = toMs(sub.current_period_end);
  const lastPaidMs = toMs(sub.current_period_start);
  const cancelAtPeriodEnd = !!sub.cancel_at_period_end;
  const paused = !!sub.pause_collection;
  let willRenew;
  let status;
  let expirationMs;
  switch (sub.status) {
    case 'active':
    case 'trialing':
      willRenew = !cancelAtPeriodEnd && !paused;
      status = paused ? STATUS.PAUSED : cancelAtPeriodEnd ? STATUS.CANCELLED : STATUS.ACTIVE;
      expirationMs = periodEndMs;
      break;
    case 'past_due':
      willRenew = !cancelAtPeriodEnd;
      status = STATUS.BILLING_RETRY;
      expirationMs = lastPaidMs;
      break;
    case 'unpaid':
      willRenew = false;
      status = STATUS.GRACE;
      expirationMs = lastPaidMs;
      break;
    case 'canceled':
    case 'incomplete_expired':
      willRenew = false;
      status = STATUS.EXPIRED;
      expirationMs = lastPaidMs;
      break;
    case 'incomplete':
      // The subscription's INITIAL payment never succeeded — Stripe collected $0
      // but still stamps an optimistic current_period_end (creation + interval).
      // Crediting that hands out free premium for the entire unpaid period (a
      // declined-card checkout deriving as active-through-period-end — the leak
      // this case closes). No paid-through date exists, so grant nothing. Stripe
      // auto-transitions this to `incomplete_expired` ~23h later, but derivation is
      // client-listener-driven with no server trigger, so the leaked entitlement
      // would otherwise persist until the user next reopens the app.
      willRenew = false;
      status = STATUS.EXPIRED;
      expirationMs = 0;
      break;
    default:
      // Fail CLOSED on any Stripe status we don't explicitly handle (e.g. a native
      // `paused`). Unlike RevenueCat — whose events only ever describe a real
      // entitlement, so an unknown event defaults to active — Stripe writes
      // subscription docs for non-paying states too, so an unknown status must NOT
      // default to granted.
      willRenew = false;
      status = STATUS.EXPIRED;
      expirationMs = 0;
  }
  const { interval, interval_count: intervalCount } = sub.priceDetails || {};
  // Surface Stripe Portal's native cancellation-reason capture if present
  // (Firebase Stripe Extension v0.3.10+ writes these fields). The transition
  // handler in updateSubscription uses these to short-circuit the in-app
  // survey so users aren't asked twice.
  const cancellationDetails = sub.cancellation_details || null;
  return {
    source: 'stripe',
    subscription_expiration: expirationMs,
    paymentMode: 'stripe',
    paymentType: 'recurring',
    will_renew: willRenew,
    status,
    plan: interval ? planFromStripeInterval(interval, intervalCount) : null,
    derived_event_id: sub.doc_id || sub.id || null,
    // Recency for pickBestState's inactive-fallback ordering — keep it on the
    // period boundary Stripe last moved (period_end), independent of the
    // paid-through expiration above.
    derived_event_ts: periodEndMs,
    stripe_cancellation_reason: cancellationDetails?.reason || null,
    stripe_cancellation_feedback: cancellationDetails?.feedback || null,
  };
}

// Add N calendar months to an epoch-ms base, preserving Date.setMonth day-of-month
// semantics (matches the legacy one-time expiry math and Stripe's monthly rollover).
// Shared so preview and derivation add months identically.
function addMonthsMs(baseMs, months) {
  const d = new Date(baseMs);
  d.setMonth(d.getMonth() + months);
  return d.getTime();
}

// Successful, non-refunded one-time charges only. Recurring subscription invoices
// are excluded by description; refunds are excluded (refunded:true or a positive
// amount_refunded) so a refunded charge never adds time to the stacked window.
function successfulOneTimePayments(payments) {
  if (!Array.isArray(payments)) return [];
  return payments.filter(
    (p) =>
      p?.status === 'succeeded' &&
      p?.description !== 'Subscription creation' &&
      p?.description !== 'Subscription update' &&
      p?.refunded !== true &&
      !(Number(p?.amount_refunded) > 0)
  );
}

// Period length in months from a one-time price's description ("1"/"3"/"6"/"12").
function oneTimePeriodMonths(payment) {
  return parseInt(payment?.priceData?.description, 10);
}

// Stripe one-time: STACK non-refunded one-time payments into a single expiry.
// Each payment grants N months (N from the price description). Folding the history
// ascending — base = max(runningExpiry, payment.created) — means a repurchase made
// WHILE STILL ACTIVE appends to the remaining balance (early-renewal / extend),
// while a repurchase after a lapse starts fresh from the new purchase date (no
// retroactive bridging of the gap). The fold is monotonic (a new payment never
// shortens the total), idempotent, and — for a single payment — identical to the
// legacy `created + N months`, so existing users don't move.
export function deriveStripeOneTimeState(payments) {
  const successful = successfulOneTimePayments(payments);
  if (successful.length === 0) return null;
  // Sort ascending by created; do not trust the caller's order.
  const ascending = [...successful].sort((a, b) => toMs(a.created) - toMs(b.created));
  let running = 0;
  let latestCounted = null; // newest payment that actually CONTRIBUTED to the expiry
  for (const p of ascending) {
    const months = oneTimePeriodMonths(p);
    if (Number.isNaN(months)) continue; // skip a malformed price description
    const base = Math.max(running, toMs(p.created));
    running = addMonthsMs(base, months);
    latestCounted = p;
  }
  if (!latestCounted) return null; // no payment had a usable period
  // Audit/display fields come from the newest CONTRIBUTING payment (not the raw
  // newest), so a malformed trailing charge can't null `plan` or point
  // derived_event_ts at a non-contributing payment — which would make
  // isSubscriptionMetadataComplete() reject an otherwise-valid active subscriber.
  // derived_event_id still moves whenever a new valid payment lands, so the web
  // no-op guard keeps noticing real purchases.
  const latestMonths = oneTimePeriodMonths(latestCounted);
  return {
    source: 'stripe-one-time',
    subscription_expiration: running,
    paymentMode: 'stripe',
    paymentType: 'one_time',
    will_renew: false,
    status: isActiveAt(running) ? STATUS.ONE_TIME : STATUS.EXPIRED,
    plan: `${latestMonths}-month`,
    derived_event_id: latestCounted.id || null,
    derived_event_ts: toMs(latestCounted.created),
  };
}

// Can this subscription be extended early (append time via a new one-time purchase)?
// The SINGLE gate for the Profile "Extend" button, the paywall checkout re-check,
// and the near-expiry nudge — so UI and enforcement can't drift. TRUE only for a
// genuine, still-active Stripe one-time purchase with no manual override, which
// excludes: recurring subs (auto-renew), license/bulk grants (paymentMode
// 'BULK_PURCHASE'), comps / legacy hand-grants (manual_override), white-label
// per-exam records, and already-lapsed one-time buyers (they just repurchase).
export function isExtendEligible(sub, now = Date.now()) {
  if (!sub) return false;
  if (sub.manual_override === true) return false;
  if (sub.status !== STATUS.ONE_TIME) return false;
  if (sub.paymentMode !== 'stripe') return false;
  if (sub.paymentType !== 'one_time') return false;
  return isActiveAt(toMs(sub.subscription_expiration), now);
}

// The expiration a user WOULD get by buying `periodMonths` more right now. Computed
// by appending a hypothetical `now` payment to their real payment history and running
// the SAME fold as derivation — so the previewed date is GUARANTEED to equal the value
// actually stored post-purchase (no divergent second code path that could promise a
// date derivation won't produce). Returns null when there is no usable period.
export function previewExtendedExpiration(payments, periodMonths, now = Date.now()) {
  const months = parseInt(periodMonths, 10);
  if (Number.isNaN(months) || months <= 0) return null;
  const hypothetical = {
    id: '__preview__',
    status: 'succeeded',
    created: now,
    priceData: { description: String(months) },
  };
  const base = Array.isArray(payments) ? payments : [];
  const derived = deriveStripeOneTimeState([...base, hypothetical]);
  return derived ? derived.subscription_expiration : null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Near-expiry "renew early" nudge state for an extend-eligible one-time buyer.
// Returns the tier bucket (T14 for 7 < daysLeft <= 14, T7 for 0 < daysLeft <= 7) so
// the home nudge can escalate copy as expiry approaches. show:false for anyone not
// extend-eligible (recurring, lapsed, comp, license, ...) or outside the window.
// daysLeft is rounded UP to match how the Profile expiry row counts days.
export function getExpiryNudgeState(sub, now = Date.now(), { t14 = 14, t7 = 7 } = {}) {
  if (!isExtendEligible(sub, now)) return { show: false, daysLeft: null, tier: null };
  const expMs = toMs(sub.subscription_expiration);
  const daysLeft = Math.ceil((expMs - now) / MS_PER_DAY);
  let tier = null;
  if (daysLeft > 0 && daysLeft <= t7) tier = 'T7';
  else if (daysLeft > t7 && daysLeft <= t14) tier = 'T14';
  return { show: tier !== null, daysLeft, tier };
}

// Pick the best state across sources. Prefer any active source with the latest
// expiration; otherwise the most recently-observed inactive source.
export function pickBestState(states, now = Date.now()) {
  const valid = states.filter((s) => s && s.subscription_expiration);
  if (valid.length === 0) return null;
  const active = valid.filter((s) => s.subscription_expiration > now);
  if (active.length > 0) {
    return active.sort((a, b) => b.subscription_expiration - a.subscription_expiration)[0];
  }
  return valid.sort((a, b) => (b.derived_event_ts || 0) - (a.derived_event_ts || 0))[0];
}

// Classify the user's current subscription stage for UI gating (win-back
// banner, resubscribe nudges, etc). 'churned' relies on the fact that the
// derived `status` field sticks past expiration — the latest RC/Stripe event
// remains the cancel — so a churned user keeps status='cancelled' (or
// 'expired') even when subscription_expiration is in the past.
// 'refunded' is its OWN terminal stage (NOT churned) so callers can exclude it:
// a refunded user must never be pitched the win-back discount.
// Never-subscribed users have no status field and fall through to that bucket.
export function getSubscriptionStage(subscription, isActive) {
  // Refunded is terminal — check it FIRST, before the isActive branch, so a
  // refund that left expiration_at_ms in the future (partial refund / access
  // continues) still reads as 'refunded' rather than 'premium_active'.
  if (subscription?.status === STATUS.REFUNDED) return 'refunded';
  if (isActive) {
    if (subscription?.status === STATUS.CANCELLED) return 'cancelled_active';
    if (subscription?.status === STATUS.PAUSED) return 'paused_active';
    return 'premium_active';
  }
  if (
    subscription?.status &&
    [STATUS.CANCELLED, STATUS.EXPIRED].includes(subscription.status)
  ) {
    return 'churned';
  }
  return 'never_subscribed';
}

// Canonical presentation state for the profile/settings subscription card,
// shared by web (PremiumDetails) and both mobile screens so the cancel/billing
// copy can't drift across the three call sites. Returns the SINGLE most-severe
// state, in the same precedence web uses: refunded → billing_retry → expired →
// paused → cancelled → one_time → active. `will_renew === false` is treated as a
// cancellation signal ONLY after the more-severe statuses are ruled out — it is
// also true for paused/expired/refunded, so collapsing everything into
// 'cancelled' (the bug this fixes) would mislabel a resumable paused sub as
// canceled. Returns 'none' for a missing/empty subscription.
export function getSubscriptionUiState(subscription, now = Date.now()) {
  if (!subscription || !subscription.subscription_expiration) return 'none';
  const { status, will_renew: willRenew, paymentType } = subscription;
  if (status === STATUS.REFUNDED) return 'refunded';
  if (status === STATUS.BILLING_RETRY) return 'billing_retry';
  // Status-aware gate (grants billing_retry above, denies refunded/expired/grace
  // even with a future expiration). Anything it denies here reads as 'expired'.
  if (!isPremiumSubscription(subscription, now)) return 'expired';
  if (status === STATUS.PAUSED) return 'paused';
  if (paymentType === 'recurring' && (status === STATUS.CANCELLED || willRenew === false)) {
    return 'cancelled';
  }
  if (paymentType === 'one_time' || status === STATUS.ONE_TIME) return 'one_time';
  return 'active';
}
